import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { faSparkle } from '@fortawesome/pro-regular-svg-icons';

import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';
import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';
import { Instructions } from './instructions';
import { streamGeneration } from './helpers';

import './styles.scss';

interface Props {
  aiEnabled?: boolean;
  // Set where the record already carries everything a first draft needs, so an empty field drafts
  // the moment it is opened. A field with text in it still waits to be told how to revise it.
  autoDraft?: boolean;
  // The field this wraps. It renders itself, so it can be a textarea, a rich text editor, or
  // anything else — it just has to go uneditable while `generating`, and put `className` wherever
  // its own background lives so the field shimmers while text arrives.
  children: (state: { className: string; generating: boolean }) => React.ReactNode;
  // Change to collapse the instruction row — e.g. after the form it sits in has been submitted.
  collapseKey?: string | number;
  // Read when generation starts rather than on render, so a form whose values live in the DOM
  // rather than in React can hand over a reader instead of scraping itself on every keystroke.
  context: Record<string, unknown> | (() => Record<string, unknown>);
  feature: string;
  format?: 'html' | 'text';
  value: string;
  onChange: (value: string) => void;
  onGeneratingChange?: (generating: boolean) => void;
}

export function AiField({
  aiEnabled = true,
  autoDraft = false,
  children,
  collapseKey,
  context,
  feature,
  format = 'text',
  value,
  onChange,
  onGeneratingChange,
}: Props) {
  const [instructionOpen, setInstructionOpen] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [generating, setGenerating] = useState(false);
  // Held for as long as the instruction row stays open, so each follow-up revises the last draft
  // inside the same conversation instead of starting a cold one.
  const [chatId, setChatId] = useState<number | undefined>();
  const [prevCollapseKey, setPrevCollapseKey] = useState(collapseKey);
  const { subscribe, unsubscribe } = useOneOffSubscription();

  const hasText = value.trim().length > 0;
  const draftsUnprompted = autoDraft && !hasText;

  useEffect(() => {
    onGeneratingChange?.(generating);
  }, [generating, onGeneratingChange]);

  if (collapseKey !== prevCollapseKey) {
    setPrevCollapseKey(collapseKey);
    closeInstructions();
  }

  if (!aiEnabled) {
    return children({ className: '', generating: false });
  }

  function closeInstructions() {
    stop();
    setInstructionOpen(false);
    setInstruction('');
    setChatId(undefined);
  }

  function openInstructions() {
    setInstructionOpen(true);

    if (draftsUnprompted) {
      generate('');
    }
  }

  function generate(prompt: string) {
    if (generating || (!prompt.trim() && !draftsUnprompted)) {
      return;
    }

    setGenerating(true);
    const original = value;

    streamGeneration({
      chatId,
      context: context instanceof Function ? context() : context,
      currentText: value,
      feature,
      format,
      instruction: prompt,
      subscribe,
      onDelta: onChange,
      onDone(text, id) {
        onChange(text);
        setChatId(id);
        setGenerating(false);
        setInstruction('');
      },
      onError: () => fail(original),
    });
  }

  function fail(original: string) {
    // Put back what the deltas overwrote, so a failure part-way through doesn't leave the user with
    // a fragment where their own text used to be.
    onChange(original);
    setGenerating(false);
    showSnackbar({ text: 'Otto could not write that. Try again.', variant: 'danger' });
  }

  // Whatever streamed in before the user hit stop is theirs to keep and edit.
  function stop() {
    unsubscribe();
    setGenerating(false);
  }

  const sparkleLabel = hasText ? 'Revise with Otto' : 'Generate with Otto';

  return (
    <div className="d-flex flex-column gap-2">
      <div className="position-relative">
        {children({
          // Reserves the sparkle some space so the field's text never runs under it
          className: classNames('pe-10', generating && 'bg-gradient-ai-animated border-purple-heart-400'),
          generating,
        })}
        <Button
          aria-label={sparkleLabel}
          className="ai-field-sparkle position-absolute bottom-0 end-0 m-2 text-purple-heart-500"
          disabled={generating}
          icon={faSparkle}
          size="sm"
          tooltip={sparkleLabel}
          variant="ghost"
          onClick={() => (instructionOpen ? closeInstructions() : openInstructions())}
        />
      </div>

      {instructionOpen && (
        <Instructions
          generating={generating}
          hasText={hasText}
          instruction={instruction}
          onChange={setInstruction}
          onPreset={generate}
          onStop={stop}
          onSubmit={() => generate(instruction)}
        />
      )}
    </div>
  );
}
